import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Reveal from "@/components/site/Reveal";

const FAQS = [
  {
    q: "¿En qué se diferencia el diagnóstico de la consultoría?",
    a: (
      <>
        <p className="mb-3">
          Talent Health Check es el nombre global de nuestro sistema de análisis del talento, y se
          contrata en dos niveles. El primero es el diagnóstico rápido (€47 / ≈$54 USD), que te dice
          QUÉ áreas de tu gestión de talento están fallando en pocos minutos.
        </p>
        <p>
          El segundo nivel es la consultoría premium (€397 / $460 USD): incluye el diagnóstico
          rápido sin coste adicional, analiza en profundidad tu empresa con un cuestionario adicional de 35 preguntas
          (37 si tu equipo es internacional),
          te entrega un informe profesional en 24 horas, una checklist interactiva editable y 7
          herramientas inteligentes entrenadas con la realidad de tu empresa.
        </p>
      </>
    ),
  },
  {
    q: "¿El análisis es genérico o automático?",
    a: "El análisis sigue una metodología que he diseñado personalmente, con base en marcos académicos de psicología organizacional y el marco legal de tu país. La tecnología es lo que permite entregarte ese análisis en 24 horas en lugar de en semanas, pero el criterio y la estructura son los que definen el resultado, no una plantilla genérica. El análisis se construye siempre a partir de los datos concretos que tú aportas sobre tu empresa, y cuando aplica, incorpora también las particularidades de tu sector.",
  },
  {
    q: "¿Qué es el contraste de percepciones de la consultoría?",
    a: "Al completar la Consultoría Premium puedes invitar a tu equipo a dar su propia perspectiva sobre la gestión del talento, de forma totalmente anónima. La plataforma contrasta esas respuestas con tu diagnóstico y te muestra coincidencias, puntos ciegos y posibles falsas alarmas. Es opcional y se activa a partir de un tamaño mínimo de equipo, para garantizar que nadie pueda ser identificado.",
  },
  {
    q: "¿Cuánto tiempo necesito para completar el diagnóstico?",
    a: "Pocos minutos. Son 30 preguntas sobre situaciones reales de gestión de talento, y puedes responderlas desde cualquier dispositivo. Al terminar recibes tus resultados al instante en un informe PDF.",
  },
  {
    q: "¿Necesito tener un departamento de RRHH para que esto me sirva?",
    a: "No. De hecho, está pensado precisamente para startups de entre 5 y 100 personas, con especial foco en las que están entre 10 y 50, que aún no tienen un departamento de RRHH estructurado y quieren tomar mejores decisiones sobre su equipo antes (o en paralelo) a implantar un software de gestión de personas.",
  },
  {
    q: "¿OptimizaRRHH compite con un software de gestión de personas?",
    a: "No. OptimizaRRHH es el paso previo. Un software de gestión de personas digitaliza los procesos que ya tienes. OptimizaRRHH construye la base metodológica (perfiles de competencias, procesos de onboarding, sistemas de evaluación, criterios de crecimiento) para que ese software tenga algo real que gestionar.",
  },
  {
    q: "¿Hay suscripciones o pagos recurrentes?",
    a: "Ninguno. Tanto el diagnóstico como la consultoría son de pago único. Pagas una vez y ya está — sin letras pequeñas ni cargos recurrentes.",
  },
  {
    q: "¿Cómo trabajamos si todo es asíncrono?",
    a: "Sin reuniones innecesarias. Completas la información cuando te va bien, y yo te entrego el análisis y los entregables en 24 horas (consultoría premium) o al instante (diagnóstico). Todo por escrito, claro y accionable.",
  },
  {
    q: "¿Dónde compro y accedo a los productos?",
    a: "La compra y el acceso ocurren en talenthealthcheck.com. Desde optimizarrhh.com te dirigimos allí para completar el proceso de pago y empezar.",
  },
  {
    q: "¿Y si compro y no me sirve?",
    a: "Tienes 14 días naturales desde la compra para desistir sin dar explicaciones: escríbeme y te devuelvo el importe íntegro por el mismo medio de pago. Eso sí, si pides que el diagnóstico se entregue de inmediato y ya has recibido tus resultados completos, ese derecho se pierde, lo tienes detallado en el aviso legal.",
  },
  {
    q: "¿Sirve si mi empresa no está en España?",
    a: "Sí. OptimizaRRHH trabaja con startups de España y Latinoamérica. Los marcos de referencia legales y las prácticas se adaptan al país de tu empresa, y los importes se muestran en euros con su equivalente aproximado en dólares.",
  },
  {
    q: "¿Qué hacéis con mis datos?",
    a: (
      <>
        Solo los uso para enviarte tu resultado, tu informe y las comunicaciones que has aceptado
        recibir, y puedes darte de baja en cualquier momento respondiendo "baja" a cualquier
        email. Nunca cedo tus datos a terceros. Tienes todo el detalle en la{" "}
        <Link to="/privacidad" className="text-[#085041] link-underline">
          política de privacidad
        </Link>
        .
      </>
    ),
  },
];

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative bg-[#F7F9F8] text-[#06231D] py-28 lg:py-40 grain scroll-mt-24"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-12 lg:mb-16">
          <Reveal>
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="font-serif-italic text-[#085041] text-[9rem] lg:text-[12rem] leading-none block">
                08
              </span>
              <span className="inline-block text-xs lg:text-sm uppercase tracking-[0.18em] font-medium text-[#F7F9F8] bg-[#06231D] px-3 py-2 rounded-md">
                Dudas
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-[20ch] mx-auto">
              Preguntas <span className="font-serif-italic text-[#085041] font-normal">frecuentes</span>.
            </h2>
          </Reveal>
        </div>

        <div className="max-w-4xl mx-auto">

            <Reveal delay={0.1}>
              <div className="relative rounded-3xl border border-[#C9A84C]/30 bg-[linear-gradient(160deg,#FFFFFF,#F0F5F1)] shadow-[0_50px_110px_-45px_rgba(6,35,29,0.3)] p-6 lg:p-10">
                <span className="absolute top-4 left-4 w-4 h-px bg-[#C9A84C]" />
                <span className="absolute top-4 left-4 w-px h-4 bg-[#C9A84C]" />
                <span className="absolute top-4 right-4 w-4 h-px bg-[#C9A84C]" />
                <span className="absolute top-4 right-4 w-px h-4 bg-[#C9A84C]" />
                <span className="absolute bottom-4 left-4 w-4 h-px bg-[#C9A84C]" />
                <span className="absolute bottom-4 left-4 w-px h-4 bg-[#C9A84C]" />
                <span className="absolute bottom-4 right-4 w-4 h-px bg-[#C9A84C]" />
                <span className="absolute bottom-4 right-4 w-px h-4 bg-[#C9A84C]" />

                <Accordion type="single" collapsible className="w-full">
                  {FAQS.map((item, i) => (
                    <AccordionItem
                      key={i}
                      value={`faq-${i}`}
                      className={`border-b border-[#06231D]/10 ${i === FAQS.length - 1 ? "border-b-0" : ""}`}
                      data-testid={`faq-item-${i}`}
                    >
                      <AccordionTrigger className="py-6 lg:py-7 text-left text-lg lg:text-xl font-display font-medium text-[#06231D] hover:no-underline hover:text-[#085041] [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-[#C9A84C] [&>svg]:flex-shrink-0">
                        <span className="flex items-baseline gap-4 pr-2">
                          <span className="font-serif-italic text-[#C9A84C] text-base lg:text-lg font-normal flex-shrink-0">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          {item.q}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-7 pl-9 lg:pl-10 text-[#06231D]/70 text-base leading-relaxed font-light text-justify hyphens-auto">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </Reveal>
        </div>
      </div>
    </section>
  );
}
